import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Box,
  IconButton,
  Button,
  Container,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight, Menu as MenuIcon } from "lucide-react";

import MegaMenu from "../../ui/MegaMenu";
import FullScreenMenu from "../../components/Shared/FullScreenMenu";
const ScrambleText = ({ text }: { text: string }) => <span>{text}</span>;

const DROPDOWN_CLOSE_DELAY_MS = 220;

const NAV_FONT = "Jost, sans-serif";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [isFullMenuOpen, setIsFullMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [hoveredCaret, setHoveredCaret] = useState<string | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const theme = useTheme();
  // Using 'lg' breakpoint to switch to mobile menu on smaller screens
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const windowHeight = window.innerHeight;
      const documentHeight =
        document.documentElement.scrollHeight - windowHeight;
      const scrollTop = window.scrollY;
      const progress = (scrollTop / documentHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isFullMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isFullMenuOpen]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const navLinks = [
    { name: "Solutions", hasCaret: true },
    { name: "Industries", hasCaret: true },
    { name: "Portfolio", hasCaret: false },
    { name: "About", hasCaret: false },
    { name: "Contact", hasCaret: false },
  ];

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleMegaMenuClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setIsMegaMenuOpen(false);
      setHoveredLink(null);
      setHoveredCaret(null);
      setActiveMenu(null);
      closeTimeoutRef.current = null;
    }, DROPDOWN_CLOSE_DELAY_MS);
  };

  const closeMegaMenuNow = () => {
    clearCloseTimeout();
    setIsMegaMenuOpen(false);
    setHoveredLink(null);
    setHoveredCaret(null);
    setActiveMenu(null);
  };

  const handleMenuOpen = (linkName: string) => {
    clearCloseTimeout();

    setHoveredLink(linkName);

    if (linkName === "Solutions" || linkName === "Industries") {
      setIsMegaMenuOpen(true);
      setHoveredCaret(linkName);
      setActiveMenu(linkName);
    } else {
      setIsMegaMenuOpen(false);
      setHoveredCaret(null);
      setActiveMenu(null);
    }
  };

  const handleMenuClose = (linkName: string) => {
    if (linkName === "Solutions" || linkName === "Industries") {
      scheduleMegaMenuClose();
      return;
    }

    setHoveredLink(null);
  };

  const handleMegaMenuEnter = () => {
    clearCloseTimeout();

    if (activeMenu) {
      setHoveredLink(activeMenu);
      setHoveredCaret(activeMenu);
    }

    setIsMegaMenuOpen(true);
  };

  const handleMegaMenuLeave = () => {
    scheduleMegaMenuClose();
  };

  return (
    <>
      <AppBar
        component={motion.nav}
        position="fixed"
        elevation={0}
        initial={false}
        animate={{
          backgroundColor: isFullMenuOpen
            ? "#FFFFFF"
            : isMegaMenuOpen
              ? "rgba(0, 0, 0, 0.95)"
              : isScrolled
                ? "rgba(0, 0, 0, 0.85)"
                : "rgba(0, 0, 0, 0)",
          backdropFilter:
            (isScrolled || isMegaMenuOpen) && !isFullMenuOpen
              ? "blur(16px)"
              : "none",
          borderBottom:
            (isScrolled || isMegaMenuOpen) && !isFullMenuOpen
              ? "1px solid rgba(255,255,255,0.05)"
              : "1px solid rgba(255,255,255,0)",
        }}
        transition={{ duration: 0.5 } as any}
        sx={{
          height: "88px",
          justifyContent: "center",
          boxShadow: "none",
          borderBottom: "1px solid transparent",
        }}
      >
        {/* Kinetic Scroll Progress Stream */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "2px",
            bgcolor: "rgba(255,255,255,0.05)",
            overflow: "hidden",
            zIndex: 20,
          }}
        >
          <motion.div
            style={{
              height: "100%",
              backgroundColor: "#E80029",
              width: `${scrollProgress}%`,
              boxShadow: "0 0 10px #E80029",
            }}
          />
        </Box>

        <Container
          maxWidth={false}
          sx={{
            maxWidth: "1920px",
            px: { xs: 3, md: 6 },
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
            zIndex: 10,
          }}
        >
          {/* Logo */}
          <Box
            component={RouterLink}
            to="/"
            sx={{ display: "block", textDecoration: "none" }}
          >
            <motion.div
              style={{
                width: isMobile ? "140px" : "200px",
                height: isMobile ? "28px" : "40px",
                cursor: "pointer",
              }}
              animate={{
                filter: isFullMenuOpen ? "invert(1)" : "invert(0)",
              }}
              transition={{ duration: 0.2 }}
            >
              {/* Using the existing logo format from your project */}
              <img
                src="/rw h MADAR AERO@0.5x.webp"
                alt="Madar Logo"
                style={{ width: "100%", height: "auto" }}
              />
            </motion.div>
          </Box>

          {/* Center Nav Links - Desktop */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
              {navLinks.map((link) => (
                <Box
                  key={link.name}
                  sx={{
                    position: "relative",
                    height: "88px",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onMouseEnter={() => handleMenuOpen(link.name)}
                  onMouseLeave={() => handleMenuClose(link.name)}
                >
                  {link.hasCaret && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: -16,
                        right: -16,
                        bottom: -16,
                        height: 16,
                      }}
                    />
                  )}

                  <RouterLink
                    to={`/${link.name.toLowerCase()}`}
                    style={{
                      textDecoration: "none",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                    className="group"
                  >
                    <motion.div
                      style={{
                        position: "relative",
                        fontSize: "12px",
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        fontWeight: "bold",
                        fontFamily: NAV_FONT,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                      animate={{
                        color: isFullMenuOpen
                          ? "#000000"
                          : hoveredLink === link.name
                            ? "#E80029"
                            : "rgba(255, 255, 255, 0.7)",
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      {hoveredLink === link.name && !isFullMenuOpen ? (
                        <ScrambleText text={link.name} />
                      ) : (
                        link.name
                      )}

                      {link.hasCaret && (
                        <motion.div
                          animate={{
                            rotate:
                              hoveredCaret === link.name ||
                                (isMegaMenuOpen && activeMenu === link.name)
                                ? 180
                                : 0,
                            y:
                              hoveredCaret === link.name ||
                                (isMegaMenuOpen && activeMenu === link.name)
                                ? -1
                                : 1,
                          }}
                          transition={{
                            duration: 0.4,
                            type: "spring",
                            stiffness: 200,
                            damping: 15,
                          }}
                        >
                          <ChevronDown
                            size={12}
                            className={
                              isFullMenuOpen ? "text-black/70" : "text-white/40"
                            }
                          />
                        </motion.div>
                      )}
                    </motion.div>

                    {/* Indicators */}
                    {hoveredLink === link.name && (
                      <>
                        {/* Bottom underline */}
                        <motion.div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: "2px",
                            background:
                              "linear-gradient(90deg, transparent, #E80029, transparent)",
                            transformOrigin: "center",
                          }}
                          layoutId="underline"
                        />
                        {/* Diamond */}
                        <motion.div
                          style={{
                            position: "absolute",
                            bottom: "-1px",
                            left: "50%",
                            width: "4px",
                            height: "4px",
                            backgroundColor: "#E80029",
                            x: "-50%",
                            clipPath:
                              "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
                            boxShadow: "0 0 8px #E80029",
                          }}
                          layoutId="diamond"
                        />
                        {/* Reticles */}
                        <motion.div
                          style={{
                            position: "absolute",
                            top: 25,
                            left: -12,
                            width: 8,
                            height: 8,
                            borderTop: "1px solid rgba(232, 0, 41, 0.5)",
                            borderLeft: "1px solid rgba(232, 0, 41, 0.5)",
                          }}
                          initial={{ opacity: 0, x: 5, y: 5 }}
                          animate={{ opacity: 1, x: 0, y: 0 }}
                        />
                        <motion.div
                          style={{
                            position: "absolute",
                            bottom: 25,
                            right: -12,
                            width: 8,
                            height: 8,
                            borderBottom: "1px solid rgba(232, 0, 41, 0.5)",
                            borderRight: "1px solid rgba(232, 0, 41, 0.5)",
                          }}
                          initial={{ opacity: 0, x: -5, y: -5 }}
                          animate={{ opacity: 1, x: 0, y: 0 }}
                        />
                      </>
                    )}
                  </RouterLink>
                </Box>
              ))}
            </Box>
          )}

          {/* Right Side - CTA */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            {/* Mobile Toggle */}
            {isMobile && (
              <IconButton
                onClick={() => setIsFullMenuOpen(!isFullMenuOpen)}
                sx={{ color: isFullMenuOpen ? "#000" : "#fff" }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Login CTA - Desktop */}
            {!isMobile && (
              <Box onMouseEnter={closeMegaMenuNow}>
                <MissionCTA onClick={() => navigate("/login")} />
              </Box>
            )}          </Box>
        </Container>
      </AppBar>

      {/* Mega Menu & Full Screen Menu Placeholders (Hidden for now until implemented) */}
      <MegaMenu
        isOpen={isMegaMenuOpen && !isFullMenuOpen}
        onClose={closeMegaMenuNow}
        onMouseEnter={handleMegaMenuEnter}
        onMouseLeave={handleMegaMenuLeave}
        activeMenu={activeMenu}
      />

      <FullScreenMenu
        isOpen={isFullMenuOpen}
        onClose={() => setIsFullMenuOpen(false)}
      />
    </>
  );
}

// Login CTA Button Component
function MissionCTA({ onClick }: { onClick: () => void }) {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <Button
      onClick={onClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      sx={{
        position: "relative",
        overflow: "hidden",
        height: "44px",
        p: 0,
        minWidth: 0,
        borderRadius: 0,
        border: "none",
        bgcolor: "transparent",
        "&:hover": {
          bgcolor: "transparent",
        },
      }}
    >
      {/* Decorative Corners */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "8px",
          height: "8px",
          borderTop: "1px solid rgba(255,255,255,0.5)",
          borderLeft: "1px solid rgba(255,255,255,0.5)",
          transition: "border-color 0.5s",
          borderColor: isHovering ? "#E80029" : "rgba(255,255,255,0.5)",
          zIndex: 20,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "8px",
          height: "8px",
          borderBottom: "1px solid rgba(255,255,255,0.5)",
          borderRight: "1px solid rgba(255,255,255,0.5)",
          transition: "border-color 0.5s",
          borderColor: isHovering ? "#E80029" : "rgba(255,255,255,0.5)",
          zIndex: 20,
        }}
      />

      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          height: "100%",
          border: "1px solid rgba(255,255,255,0.1)",
          borderColor: isHovering
            ? "rgba(232, 0, 41, 0.5)"
            : "rgba(255,255,255,0.1)",
          transition: "border-color 0.5s",
          bgcolor: "#000000",
          overflow: "hidden",
        }}
      >
        {/* Kinetic Hover Background Fill */}
        <motion.div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "#E80029",
            transformOrigin: "left",
            zIndex: 0,
          }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: isHovering ? 1 : 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        />

        {/* Text Segment - Slot machine effect showing LOGIN on both states */}
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
            position: "relative",
            zIndex: 10,
            overflow: "hidden",
            width: "150px",
          }}
        >
          <Box
            component={motion.span}
            sx={{
              color: "#fff",
              fontSize: "11px",
              letterSpacing: "0.2em",
              fontWeight: "bold",
              whiteSpace: "nowrap",
              fontFamily: NAV_FONT,
              display: "block",
              position: "absolute",
            }}
            animate={{ y: isHovering ? -30 : 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            LOGIN
          </Box>

          <Box
            component={motion.span}
            sx={{
              color: "#fff",
              fontSize: "11px",
              letterSpacing: "0.2em",
              fontWeight: "bold",
              whiteSpace: "nowrap",
              fontFamily: NAV_FONT,
              display: "block",
              position: "absolute",
            }}
            initial={{ y: 30 }}
            animate={{ y: isHovering ? 0 : 30 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            LOGIN
          </Box>
        </Box>

        {/* Right Arrow Segment */}
        <Box
          sx={{
            height: "100%",
            width: "44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderLeft: "1px solid rgba(255,255,255,0.1)",
            borderColor: isHovering
              ? "rgba(255,255,255,0.2)"
              : "rgba(255,255,255,0.1)",
            position: "relative",
            zIndex: 10,
            bgcolor: "rgba(255,255,255,0.05)",
            transition: "all 0.5s",
          }}
        >
          <motion.div
            animate={{
              rotate: isHovering ? -45 : 0,
            }}
            transition={{ duration: 0.3, type: "spring", stiffness: 200 }}
          >
            <ArrowRight size={16} color="white" />
          </motion.div>
        </Box>
      </Box>
    </Button>
  );
}
